import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, Trash2, Search } from "lucide-react";

const STATUSES = ["pending", "shipped", "delivered", "cancelled"];

const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-yellow-400 text-black",
    shipped: "bg-blue-500 text-white",
    delivered: "bg-green-500 text-black",
    cancelled: "bg-red-500 text-white",
  };
  return (
    <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-widest ${colors[status] || "bg-zinc-700 text-white"}`}>
      {status}
    </span>
  );
};

const OrderRow = ({ order, onChanged }) => {
  const [open, setOpen] = useState(false);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(`Ordine ${status}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questo ordine?")) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success("Ordine eliminato");
      onChanged();
    } catch (err) {
      toast.error("Errore");
    }
  };

  return (
    <>
      <tr data-testid={`order-row-${order.id}`} className="border-b border-zinc-800 hover:bg-zinc-950/50">
        <td className="p-3 font-mono text-yellow-400 text-xs">#{order.id.slice(0, 8).toUpperCase()}</td>
        <td className="p-3">
          <div className="text-white font-bold">{order.customer_name}</div>
          <div className="text-zinc-500 text-xs">{order.customer_email}</div>
        </td>
        <td className="p-3 text-zinc-400 text-sm">{new Date(order.created_at).toLocaleDateString("it-IT")}</td>
        <td className="p-3 text-yellow-400 font-bold">€ {order.total.toFixed(2)}</td>
        <td className="p-3">
          <StatusBadge status={order.status} />
          {order.payment_status && (
            <div className="mt-1">
              <span data-testid={`payment-status-badge-${order.id}`} className={`px-2 py-0.5 text-[9px] font-heading uppercase tracking-widest ${
                order.payment_status === "paid" ? "bg-green-500 text-black" : order.payment_status === "failed" ? "bg-red-500 text-white" : "bg-zinc-700 text-zinc-300"
              }`}>
                {order.payment_status === "paid" ? "Pagato" : order.payment_status === "failed" ? "Fallito" : "Da pagare"}
              </span>
            </div>
          )}
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <select
              data-testid={`status-select-${order.id}`}
              value={order.status}
              onChange={(e) => updateStatus(e.target.value)}
              className="bg-zinc-800 border-2 border-zinc-700 text-white text-xs px-2 py-1 focus:border-yellow-400 focus:outline-none uppercase"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button data-testid={`expand-order-${order.id}`} onClick={() => setOpen(!open)} className="bg-zinc-800 text-yellow-400 p-1.5 hover:bg-yellow-400 hover:text-black transition">
              <ChevronDown className={`w-4 h-4 transition ${open ? "rotate-180" : ""}`} strokeWidth={3} />
            </button>
            <button data-testid={`delete-order-${order.id}`} onClick={del} className="bg-zinc-800 text-red-400 p-1.5 hover:bg-red-600 hover:text-white transition">
              <Trash2 className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-zinc-950 border-b border-zinc-800">
          <td colSpan={6} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-2">Spedizione</div>
                <div className="text-white text-sm">{order.shipping_address}</div>
                <div className="text-zinc-400 text-sm">{order.postal_code} {order.city}, {order.country}</div>
              </div>
              <div>
                <div className="font-heading uppercase text-yellow-400 text-xs tracking-widest mb-2">Articoli ({order.items.length})</div>
                <ul className="space-y-1 text-sm">
                  {order.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-zinc-300">
                      <span>{it.quantity}× {it.title}</span>
                      <span className="text-yellow-400">€ {(it.price * it.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = orders
    .filter((o) => filter === "all" || o.status === filter)
    .filter((o) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return o.customer_email.toLowerCase().includes(q) || o.customer_name.toLowerCase().includes(q) || o.id.toLowerCase().includes(q);
    });

  return (
    <div data-testid="admin-orders" className="space-y-6">
      <div>
        <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">注文管理</div>
        <h1 className="font-heading text-5xl uppercase text-white leading-none">Ordini</h1>
        <p className="text-zinc-400 mt-2">{orders.length} ordini totali · {filtered.length} mostrati</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`filter-status-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 transition ${
                filter === s ? "bg-yellow-400 text-black border-yellow-400" : "border-zinc-700 text-zinc-400 hover:border-yellow-400"
              }`}
            >
              {s === "all" ? "Tutti" : s}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={3} />
          <input
            data-testid="orders-search"
            placeholder="Cerca per nome, email o ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border-2 border-zinc-800 pl-10 pr-4 py-2 text-white focus:border-yellow-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-950 border-b-2 border-zinc-800">
            <tr className="text-left">
              {["ID", "Cliente", "Data", "Totale", "Stato", "Azioni"].map((h) => (
                <th key={h} className="font-heading uppercase text-yellow-400 text-xs tracking-widest p-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-zinc-500 uppercase">Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500 uppercase">Nessun ordine</td></tr>}
            {filtered.map((o) => <OrderRow key={o.id} order={o} onChanged={load} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
